import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { MenuProps as AntdMenuProps } from 'antd/lib/menu';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Dropdown,
  Menu,
  MenuItem,
  theme,
} from 'components';
import { Icon, Text } from '@bb/elements-web';
import { ListingAvailability } from '@bb/domain/dist/types';
import { ListingLabelContext } from 'contexts';
import LayoutCard, { LayoutCardCustomProps } from './LayoutCard';

type ListingLayoutCardProps = {
  availability: string[];
  availabilityOptions: ListingAvailability[];
  onStatusChange: (status: string) => void;
  onAvailabilityChange: (availability: string[]) => void;
} & LayoutCardCustomProps;

const STATUS_DROPDOWN_KEYS = [
  { key: 'Assignment', 'data-cy': 'listings-filter-assignment' },
  { key: 'Coming Soon', 'data-cy': 'listings-filter-coming-soon' },
  { key: 'Exclusive', 'data-cy': 'listings-filter-exclusive' },
  { key: 'New Construction', 'data-cy': 'listings-filter-new-construction' },
  { key: 'MLS', 'data-cy': 'listings-filter-mls' },
];

const AVAILABILITY_DROPDOWN_KEYS: {
  key: ListingAvailability;
  'data-cy': string;
}[] = [
  {
    key: 'Sold Conditionally',
    'data-cy': 'listings-filter-sold-conditionally',
  },
  {
    key: 'Leased Conditionally',
    'data-cy': 'listings-filter-leased-conditionally',
  },
  { key: 'Price Change', 'data-cy': 'listings-filter-price-change' },
  { key: 'Extended', 'data-cy': 'listings-filter-extended' },
  { key: 'Deal Fell Through', 'data-cy': 'listings-filter-fell-through' },
  { key: 'New', 'data-cy': 'listings-filter-new' },
  { key: 'Show For Backups', 'data-cy': 'listings-filter-backups' },
  { key: 'Withdrawn', 'data-cy': 'listings-filter-withdrawn' },
  { key: 'Sold', 'data-cy': 'listings-filter-sold' },
  { key: 'Leased', 'data-cy': 'listings-filter-leased' },
  { key: 'Suspended', 'data-cy': 'listings-filter-suspended' },
  { key: 'Terminated', 'data-cy': 'listings-filter-terminated' },
  { key: 'Expired', 'data-cy': 'listings-filter-expired' },
  { key: 'Combined listing', 'data-cy': 'listings-filter-combined-listing' },
  { key: 'Pending', 'data-cy': 'listings-filter-pending' },
  { key: 'Temp Off Market', 'data-cy': 'listings-filter-off-market' },
];

const ListingLayoutCard: React.FC<ListingLayoutCardProps> = ({
  availability,
  availabilityOptions,
  onStatusChange,
  onAvailabilityChange,
  children,
  ...layoutCardProps
}) => {
  const { getLabel, isHidden } = React.useContext(ListingLabelContext);
  const [menuVisible, setMenuVisible] = useState<
    'status' | 'availability' | null
  >(null);

  const onOpenStatusChange = useCallback(
    (visible: boolean) => {
      setMenuVisible(visible ? 'status' : null);
    },
    [setMenuVisible],
  );
  const onOpenAvailabilityChange = useCallback(
    (visible: boolean) => {
      setMenuVisible(visible ? 'availability' : null);
    },
    [setMenuVisible],
  );

  const onStatusMenuItemClick: AntdMenuProps['onClick'] = useCallback(
    e => {
      setMenuVisible(null);
      onStatusChange(e.key as string);
    },
    [onStatusChange],
  );

  const handleAvailabilitySelectAll = useCallback(
    (checked?: boolean) => {
      const selectedOptions = checked ? availabilityOptions : [];
      onAvailabilityChange(selectedOptions);
    },
    [availabilityOptions, onAvailabilityChange],
  );

  const statusFilterButton = (
    <Dropdown
      trigger={['click']}
      open={menuVisible === 'status'}
      onOpenChange={onOpenStatusChange}
      dropdownRender={() => (
        <Menu
          data-cy="dashboard-listing-status-filter"
          onClick={onStatusMenuItemClick}
        >
          <MenuItem data-cy="listings-filter-all" key="All">
            All
          </MenuItem>
          {STATUS_DROPDOWN_KEYS.sort((a, b) =>
            getLabel(a.key)!.localeCompare(getLabel(b.key)!),
          ).map(
            x =>
              !isHidden(x.key) && (
                <MenuItem key={x.key} data-cy={x['data-cy']}>
                  {getLabel(x.key)}
                </MenuItem>
              ),
          )}
        </Menu>
      )}
    >
      <DropdownTextContainer>
        <StyledButton
          size="small"
          icon={<Icon icon="filterOutlined" size={16} color="black" />}
        />
      </DropdownTextContainer>
    </Dropdown>
  );

  const availabilityDropdownMenu = (
    <Card>
      <Checkbox
        data-cy="listings-filter-all"
        onChange={e => handleAvailabilitySelectAll(e.target.checked)}
        checked={availability.length === availabilityOptions.length}
      >
        All
      </Checkbox>
      <StyledCheckboxGroup
        options={AVAILABILITY_DROPDOWN_KEYS.filter(option =>
          availabilityOptions.includes(option.key),
        )
          .sort((a, b) => getLabel(a.key)!.localeCompare(getLabel(b.key)!))
          .map(x => ({
            label: <label data-cy={x['data-cy']}>{getLabel(x.key)}</label>,
            value: x.key,
          }))}
        value={availability}
        onChange={onAvailabilityChange}
      />
    </Card>
  );

  const ArrowIconComponent = Icon;
  const StyledTextComponent = Text;

  return (
    <LayoutCard
      {...layoutCardProps}
      activeTabKey="1"
      tabList={[
        {
          key: '0',
          tab: (
            <DropdownsContainer>
              <Dropdown
                trigger={['click']}
                open={menuVisible === 'availability'}
                onOpenChange={onOpenAvailabilityChange}
                dropdownRender={() => availabilityDropdownMenu}
              >
                <DropdownTextContainer>
                  <StyledTextComponent
                    size="large"
                    color={
                      menuVisible === 'availability' ? 'primary' : 'default'
                    }
                  >
                    {getLabel('Availability')}
                  </StyledTextComponent>
                  <StyledBadge
                    color={menuVisible === 'availability' ? 'primary' : 'gray8'}
                    count={availability.length}
                    defaultAlign
                    offset={[0, -2]}
                  />
                  <ArrowIconComponent
                    icon={
                      menuVisible === 'availability'
                        ? 'arrowUpOutlined'
                        : 'arrowDownOutlined'
                    }
                    color={
                      menuVisible === 'availability' ? 'primary' : 'default'
                    }
                  />
                </DropdownTextContainer>
              </Dropdown>
            </DropdownsContainer>
          ),
        },
      ]}
      tabProps={{
        size: 'small',
      }}
      titleExtra={statusFilterButton}
      titleExtraPosition="left"
    >
      {children}
    </LayoutCard>
  );
};

export default ListingLayoutCard;

const DropdownsContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const DropdownTextContainer = styled.div`
  vertical-align: bottom;
  color: ${theme.gray8};
  font-weight: 400;

  :hover {
    color: ${theme.gray8};
    cursor: pointer;
  }
`;

const StyledCheckboxGroup = styled(Checkbox.Group)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
`;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledBadge = styled(Badge)`
  margin: 0 4px;
`;
